import { httpClient } from './http-client';
import type { ApiResponse, PaginatedResponse } from './api.types';

export interface MovimientoApiDto {
  id: string;
  tipo: string;
  concepto?: string;
  monto: number;
  medioPagoId?: string;
  medioPagoNombre?: string;
  numeroOperacion?: string;
  referencia?: string;
  usuarioNombre?: string;
  createdAt: string;
}

export interface TransaccionApiDto {
  id: string;
  origen: string;
  origenId: string;
  origenNumero?: string;
  tipo: string;
  monto: number;
  medioPagoId?: string;
  medioPagoCodigo?: string;
  medioPagoNombre?: string;
  clienteNombre?: string;
  clienteDocumento?: string;
  moneda?: string;
  createdAt: string;
}

export interface ResumenApiDto {
  apertura: number;
  totalIngresos: number;
  totalEgresos: number;
  saldoActual: number;
  cantidadMovimientos: number;
  cantidadTransacciones: number;
}

export interface ResumenMedioPagoApiDto {
  medioPago: string;
  total: number;
}

export interface CajaSesionApiDto {
  id: string;
  cajaId: string;
  cajaNombre: string;
  empresaId: string;
  establecimientoId: string;
  usuarioAperturaId?: string;
  usuarioAperturaNombre: string;
  fechaApertura: string;
  montoInicial: number;
  montoInicialDetalle: Record<string, number>;
  notasApertura?: string;
  usuarioCierreId?: string;
  usuarioCierreNombre?: string;
  fechaCierre?: string;
  montoCierreEsperado?: number;
  montoCierreRealPen?: number;
  montoCierreRealUsd?: number;
  montoCierreDetalle?: Record<string, number>;
  diferenciaPen?: number;
  diferenciaUsd?: number;
  observacionCierre?: string;
  estado: string;
  resumen: ResumenApiDto;
  resumenPorMedioPago: ResumenMedioPagoApiDto[];
  movimientos: MovimientoApiDto[];
  transacciones: TransaccionApiDto[];
  createdAt: string;
  updatedAt: string;
}

export interface CajaMovimientoListadoApiDto {
  id: string;
  fuente: 'movimiento' | 'transaccion';
  tipo: string;
  concepto?: string;
  monto: number;
  medioPagoNombre?: string;
  referencia?: string;
  usuarioNombre?: string;
  clienteNombre?: string;
  moneda?: string;
  createdAt: string;
}

export interface CajaSesionListadoApiDto {
  id: string;
  cajaId: string;
  cajaNombre: string;
  empresaId: string;
  establecimientoId: string;
  usuarioAperturaNombre: string;
  fechaApertura: string;
  montoInicial: number;
  estado: string;
  fechaCierre?: string;
  diferenciaPen?: number;
  diferenciaUsd?: number;
  montoCierreEsperado?: number;
  montoCierreRealPen?: number;
  montoCierreRealUsd?: number;
  montoIngresos: number;
  montoEgresos: number;
}

export interface AbrirCajaInput {
  cajaId: string;
  establecimientoId: string;
  montoInicial: number;
  montoInicialDetalle?: Record<string, number>;
  notas?: string;
}

export interface CerrarCajaInput {
  montoCierreDetalle?: Record<string, number>;
  diferenciaPen?: number;
  diferenciaUsd?: number;
  observacion?: string;
}

export interface RegistrarMovimientoInput {
  tipo: string;
  concepto?: string;
  monto: number;
  medioPagoId?: string;
  medioPagoNombre?: string;
  numeroOperacion?: string;
  referencia?: string;
}

export interface RegistrarTransaccionInput {
  cajaId: string;
  establecimientoId: string;
  origen: string;
  origenId: string;
  origenNumero?: string;
  tipo: string;
  monto: number;
  medioPagoId?: string;
  medioPagoCodigo?: string;
  medioPagoNombre?: string;
  clienteNombre?: string;
  clienteDocumento?: string;
  moneda?: string;
}

export const cajaSesionService = {
  getSesionActiva: (params: { cajaId?: string; establecimientoId?: string }): Promise<ApiResponse<CajaSesionApiDto | null>> => {
    return httpClient.get('/caja-sesiones/activa', params);
  },

  getById: (id: string): Promise<ApiResponse<CajaSesionApiDto>> => {
    return httpClient.get(`/caja-sesiones/${id}`);
  },

  getHistorial: (params?: {
    cajaId?: string;
    establecimientoId?: string;
    page?: number;
    pageSize?: number;
    fechaDesde?: string;
    fechaHasta?: string;
    estado?: string;
  }): Promise<ApiResponse<PaginatedResponse<CajaSesionListadoApiDto>>> => {
    return httpClient.get('/caja-sesiones/historial', params);
  },

  descargarSesionesCerradasExcel: (params?: {
    empresaId?: string;
    establecimientoId?: string;
    cajaId?: string;
    fechaDesde?: string;
    fechaHasta?: string;
  }): Promise<Blob> => {
    return httpClient.obtenerBlob('/reportes/caja/sesiones/excel', params);
  },

  abrir: (data: AbrirCajaInput): Promise<ApiResponse<CajaSesionApiDto>> => {
    return httpClient.post('/caja-sesiones/abrir', data);
  },

  cerrar: (sesionId: string, data: CerrarCajaInput): Promise<ApiResponse<CajaSesionApiDto>> => {
    return httpClient.patch(`/caja-sesiones/${sesionId}/cerrar`, data);
  },

  registrarMovimiento: (sesionId: string, data: RegistrarMovimientoInput): Promise<ApiResponse<MovimientoApiDto>> => {
    return httpClient.post(`/caja-sesiones/${sesionId}/movimientos`, data);
  },

  registrarTransaccion: (data: RegistrarTransaccionInput): Promise<ApiResponse<TransaccionApiDto | null>> => {
    return httpClient.post('/caja-sesiones/transacciones', data);
  },

  getMovimientos: (sesionId: string, params?: {
    tipo?: string;
    medioPago?: string;
    fechaDesde?: string;
    fechaHasta?: string;
  }): Promise<ApiResponse<CajaMovimientoListadoApiDto[]>> => {
    return httpClient.get(`/caja-sesiones/${sesionId}/movimientos`, params);
  },

  getResumen: (sesionId: string): Promise<ApiResponse<ResumenApiDto>> => {
    return httpClient.get(`/caja-sesiones/${sesionId}/resumen`);
  },

  exportarMovimientosExcel: (params?: {
    establecimientoId?: string;
    fechaDesde?: string;
    fechaHasta?: string;
  }): Promise<Blob> => {
    return httpClient.obtenerBlob('/reportes/caja/movimientos/excel', params);
  },
};
